import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, GREEN_BTN_URL, RED_BTN_URL, DONTKNOW_BTN_URL } from "@/lib/api";
import { getTheme, DEFAULT_STYLE, bgImageStyle } from "@/lib/themes";
import { playerColor } from "@/lib/playerColors";
import { VideoBg } from "@/components/VideoBg";
import { useGameSocket } from "@/hooks/useGameSocket";
import { HitmasterLogo } from "@/components/HitmasterLogo";
import { PlayerScoreCard } from "@/components/PlayerScoreCard";
import { HomeScreenTip } from "@/components/HomeScreenTip";
import { CountUp } from "@/components/CountUp";
import { VinylReveal } from "@/components/VinylReveal";
import { GameTypeBadge } from "@/components/GameTypeBadge";
import { Equalizer } from "@/components/Equalizer";
import { playHorn, playWhistle, playMagic, playTick } from "@/lib/sfx";
import { MosaicReveal } from "@/components/MosaicReveal";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Minimize2, Maximize2, Calendar, Mic, Music2, Disc3, Star, Trophy } from "lucide-react";
import { usePersistentPlayer } from "@/components/PersistentPlayer";
import { DeviceClip } from "@/components/DeviceClip";
import { LobbyMusic } from "@/components/LobbyMusic";
import { useLang } from "@/i18n";

// Challenge YES/NO button art (used in the G1 CHALLENGE? prompt)
const CHALLENGE_YES_URL = "https://customer-assets-wrfwihn1.emergentagent.net/job_08d49250-382a-4cb1-91a3-6efa45406f9b/artifacts/y891vwu8_CHALLENGE%20YES.png";
const CHALLENGE_NO_URL = "https://customer-assets-wrfwihn1.emergentagent.net/job_08d49250-382a-4cb1-91a3-6efa45406f9b/artifacts/ibucnztt_challenge%20no.png";

export default function PlayerGame() {
  const { code } = useParams();
  const nav = useNavigate();
  const { t } = useLang();
  const stored = JSON.parse(localStorage.getItem(`hm_player_${code}`) || "null");
  const playerId = stored?.playerId;
  const myName = stored?.name;

  const [room, setRoom] = useState(null);
  const [buzzerPlayerId, setBuzzerPlayerId] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [answer, setAnswer] = useState({ artist: "", track: "", year: "" });
  const [truth, setTruth] = useState(null);
  const [verdicts, setVerdicts] = useState({});
  const [challengeUsed, setChallengeUsed] = useState(false);
  const [buzzPressed, setBuzzPressed] = useState(false);
  const [autoNextIn, setAutoNextIn] = useState(null);
  const [roundStat, setRoundStat] = useState(null);
  const [paused, setPaused] = useState(false);
  // G2-only: the answer submitted by the buzz-holder, broadcast to everyone
  const [submittedAnswer, setSubmittedAnswer] = useState(null); // {playerId, artist, track}
  const [answeredIds, setAnsweredIds] = useState([]); // playerIds who submitted this round
  // G2-only: playerId of the player who challenged (won the challenge race)
  const [challengerId, setChallengerId] = useState(null);
  // G3-only: album mosaic reveal data
  const [mosaicArtwork, setMosaicArtwork] = useState(null);
  const [mosaicOrder, setMosaicOrder] = useState([]);
  // G4-only: video-on-phone state
  const [yearChoices, setYearChoices] = useState([]);
  // G1 turn-based state
  const [activePlayerId, setActivePlayerId] = useState(null);
  const [challengeOpen, setChallengeOpen] = useState(false);
  const [passedIds, setPassedIds] = useState([]);
  const [turnEndsAt, setTurnEndsAt] = useState(null);
  const [challengeAfterPass, setChallengeAfterPass] = useState(false);
  const [challengeEndsAt, setChallengeEndsAt] = useState(null);
  const [challengeLeft, setChallengeLeft] = useState(null);
  const [challengeDeclined, setChallengeDeclined] = useState(false);
  // Resume the "Ready" state after a mid-game page reload (mobile browsers often
  // evict a backgrounded tab). Without this the player would be trapped on the
  // "Yes" gate again on every reload — looking like a hang from round N onward.
  const [audioEnabled, setAudioEnabled] = useState(() => {
    try { return sessionStorage.getItem(`hm_ready_${code}`) === "1"; } catch { return false; }
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [cssFullscreen, setCssFullscreen] = useState(false); // fallback when the Fullscreen API is blocked
  // YouTube IFrame-API player on this device (audio-only clip or G4 video).
  const clipRef = useRef(null);
  const loadedVideoRef = useRef(null); // videoId currently loaded/cued in the clip
  const syncRoundRef = useRef(null);   // round we're buffering & waiting to sync-play
  const clipReadyRef = useRef(false);
  const sendRef = useRef(null);        // stable handle to the WS send fn
  const audioEnabledRef = useRef(false); // latest audioEnabled, readable in callbacks
  const lastRoundRef = useRef(null);   // last round we reset per-round state for
  const prevWsStatus = useRef(null);   // previous socket status (for reconnect toast)

  // Track native fullscreen state (works whether entered from JoinRoom or via F11)
  useEffect(() => {
    const update = () => setIsFullscreen(!!document.fullscreenElement);
    update();
    document.addEventListener("fullscreenchange", update);
    document.addEventListener("webkitfullscreenchange", update);
    return () => {
      document.removeEventListener("fullscreenchange", update);
      document.removeEventListener("webkitfullscreenchange", update);
    };
  }, []);

  const exitFullscreen = () => {
    try {
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        const fn = document.exitFullscreen || document.webkitExitFullscreen || document.msExitFullscreen;
        if (fn) fn.call(document);
      }
    } catch {}
    setCssFullscreen(false);
  };

  const enterFullscreen = async () => {
    const el = document.documentElement;
    const fn = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
    if (fn) {
      try { await fn.call(el); return; } catch {}
    }
    // Fallback: Fullscreen API blocked (common on iOS Safari) -> cover the viewport
    // with a fixed z-9999 layer and nudge the page to hide the URL bar.
    setCssFullscreen(true);
    try { window.scrollTo(0, 1); } catch {}
  };

  const isFs = isFullscreen || cssFullscreen;
  const toggleFullscreen = () => { if (isFs) exitFullscreen(); else enterFullscreen(); };

  // countdown after REVEAL (frozen while the host paused the sequence)
  useEffect(() => {
    if (autoNextIn == null) return;
    if (autoNextIn <= 0) return;
    if (paused) return;
    const t = setTimeout(() => setAutoNextIn((n) => (n == null ? null : n - 1)), 1000);
    return () => clearTimeout(t);
  }, [autoNextIn, paused]);

  // Pull the authoritative room state from the server and reconcile all local
  // state. Used on mount AND on a short poll so players ALWAYS transition
  // (start / reveal / next round) even if a WebSocket message is missed —
  // iOS Safari in particular suspends sockets aggressively.
  const syncRoom = useCallback(async () => {
    try {
      const { data } = await api.get(`/rooms/${code}`);
      setRoom((r) => ({ ...(r || {}), ...data, gameType: data.currentGameType ?? data.gameType }));
      if (data.albumArtwork) setMosaicArtwork(data.albumArtwork);
      if (Array.isArray(data.mosaicOrder)) setMosaicOrder(data.mosaicOrder);
      if (Array.isArray(data.yearChoices)) setYearChoices(data.yearChoices);
      const to = data.turnOrder || [];
      setActivePlayerId(to.length ? to[data.activeIndex ?? 0] : null);
      setChallengeOpen(!!data.challengeOpen);
      setChallengeAfterPass(!!data.passChallengePid);
      setChallengeEndsAt(data.challengeOpen ? (data.challengeEndsAt || null) : null);
      setPassedIds(Array.isArray(data.passed) ? data.passed : []);
      setTurnEndsAt(data.challengeOpen ? null : (data.turnEndsAt || null));
      if (data.status === "reveal" && data.reveal) {
        setTruth((prev) => prev || data.reveal.truth || null);
        setVerdicts((prev) => (prev && Object.keys(prev).length ? prev : (data.reveal.verdicts || {})));
        setRoundStat((prev) => prev || data.reveal.roundStat || null);
      }
      return data;
    } catch (e) {
      return null;
    }
  }, [code]);

  useEffect(() => {
    if (!playerId) { nav(`/join?code=${code}`); return; }
    let cancelled = false;
    syncRoom().then((d) => { if (d === null && !cancelled) nav("/"); });
    const iv = setInterval(() => { if (!cancelled) syncRoom(); }, 2500);
    return () => { cancelled = true; clearInterval(iv); };
    // eslint-disable-next-line
  }, [code, playerId]);

  const { send, status } = useGameSocket({
    code,
    playerId: playerId || "spectator",
    role: "player",
    onMessage: (msg) => {
      if (msg.type === "HELLO") {
        setRoom(msg.room);
        if (msg.room?.albumArtwork) setMosaicArtwork(msg.room.albumArtwork);
        if (Array.isArray(msg.room?.mosaicOrder)) setMosaicOrder(msg.room.mosaicOrder);
        if (Array.isArray(msg.room?.yearChoices)) setYearChoices(msg.room.yearChoices);
        const to = msg.room?.turnOrder || [];
        setActivePlayerId(to.length ? to[msg.room?.activeIndex ?? 0] : null);
        setChallengeOpen(!!msg.room?.challengeOpen);
        setChallengeAfterPass(!!msg.room?.passChallengePid);
        setChallengeEndsAt(msg.room?.challengeOpen ? (msg.room?.challengeEndsAt || null) : null);
        setPassedIds(Array.isArray(msg.room?.passed) ? msg.room.passed : []);
        setTurnEndsAt(msg.room?.challengeOpen ? null : (msg.room?.turnEndsAt || null));
      }
      if (msg.type === "PLAYER_JOINED") setRoom((r) => ({ ...(r || {}), players: msg.players }));
      if (msg.type === "STYLE_CHANGED") setRoom((r) => ({ ...(r || {}), style: msg.style }));
      if (msg.type === "ROUND_STARTED") {
        setRoom((r) => ({ ...(r || {}), status: "in_round", currentRound: msg.round, gameType: msg.gameType, mode: msg.mode, audioSource: msg.audioSource || r?.audioSource || "host", playerAudio: msg.playerAudio, playedCount: msg.playedCount, totalSongs: msg.totalSongs, currentSong: msg.song }));
        setBuzzerPlayerId(null); setAnswered(false); setAnswer({ artist: "", track: "", year: "" }); setTruth(null); setVerdicts({}); setChallengeUsed(false); setAutoNextIn(null); setRoundStat(null);
        setSubmittedAnswer(null); setChallengerId(null); setAnsweredIds([]);
        setPaused(false);
        setActivePlayerId(msg.activePlayerId ?? null);
        setChallengeOpen(false);
        setChallengeAfterPass(false);
        setChallengeEndsAt(null);
        setChallengeDeclined(false);
        setPassedIds([]);
        setTurnEndsAt(msg.turnEndsAt || null);
        // G3 mosaic payload
        setMosaicArtwork(msg.albumArtwork || null);
        setMosaicOrder(msg.mosaicOrder || []);
        // G4 year choices
        setYearChoices(Array.isArray(msg.yearChoices) ? msg.yearChoices : []);
      }
      if (msg.type === "AUDIO_PLAY") {
        // Host says everyone is buffered — start playback in sync, with sound.
        syncRoundRef.current = null;
        try { clipRef.current?.unmute(); } catch {}
        try { clipRef.current?.play(); } catch {}
      }
      if (msg.type === "BUZZED") setBuzzerPlayerId(msg.playerId);
      if (msg.type === "ANSWER_SUBMITTED") {
        // Track everyone who submitted this round so we can gate the challenge button on G1
        setAnsweredIds((prev) => (prev.includes(msg.playerId) ? prev : [...prev, msg.playerId]));
        if (msg.answer) {
          // G2/G3/G4 broadcast — everyone sees the buzz-holder's answer content
          setSubmittedAnswer({ playerId: msg.playerId, ...msg.answer });
        }
      }
      if (msg.type === "CHALLENGED") {
        setChallengerId(msg.byPlayerId);
      }
      if (msg.type === "TURN_UPDATE") {
        setActivePlayerId(msg.activePlayerId ?? null);
        if (Array.isArray(msg.passed)) setPassedIds(msg.passed);
        setTurnEndsAt(msg.turnEndsAt || null);
        setChallengeOpen(false);
        setChallengeAfterPass(false);
        setChallengeEndsAt(null);
        setChallengeDeclined(false);
        setChallengerId(null);
      }
      if (msg.type === "CHALLENGE_OPEN") {
        setChallengeOpen(true);
        setChallengeAfterPass(!!msg.afterPass);
        setChallengeEndsAt(msg.challengeEndsAt || null);
        setChallengeDeclined(false);
        setTurnEndsAt(null);
      }
      if (msg.type === "ALL_PASSED") {
        setChallengeOpen(false);
        setChallengeAfterPass(false);
        setChallengeEndsAt(null);
        setTurnEndsAt(null);
      }
      if (msg.type === "REVEAL") {
        setTruth(msg.truth); setVerdicts(msg.verdicts || {});
        setRoundStat(msg.roundStat || null);
        setRoom((r) => ({ ...(r || {}), status: "reveal", players: msg.players }));
        setPaused(false);
        setChallengeOpen(false);
        setChallengeEndsAt(null);
        setChallengeDeclined(false);
        setAutoNextIn(msg.autoNextInSec ?? null);
      }
      if (msg.type === "PAUSE_STATE") setPaused(!!msg.paused);
      if (msg.type === "GAME_FINISHED") setRoom((r) => ({ ...(r || {}), status: "finished", players: msg.players || r?.players }));
    },
  });

  // Reconnect toast: when the socket drops and comes back, reassure the player.
  useEffect(() => {
    if (prevWsStatus.current === "closed" && status === "open") {
      toast.success(t("player.reconnected"));
    }
    if (status === "open" || status === "closed") prevWsStatus.current = status;
  }, [status]); // eslint-disable-line

  const me = useMemo(() => (room?.players || []).find((p) => p.id === playerId), [room, playerId]);
  const others = useMemo(() => (room?.players || []).filter((p) => p.id !== playerId).sort((a, b) => b.score - a.score), [room, playerId]);
  // Full standings (all non-bot players), highest score first — shown during reveal.
  const standings = useMemo(
    () => (room?.players || []).filter((p) => !p.isBot).slice().sort((a, b) => b.score - a.score),
    [room?.players]
  );

  // --- Turn-based Game Type 1 helpers ---
  const isTurnBased = room?.gameType === 1 && room?.mode !== "board";
  const isMyTurn = isTurnBased && activePlayerId === playerId;
  const iAmChallengerG1 = challengerId === playerId;
  const activeName = (room?.players || []).find((p) => p.id === activePlayerId)?.name || "";
  // Am I eligible to press the CHALLENGE? YES/NO prompt right now?
  const canG1Challenge = isTurnBased && room?.status === "in_round" && challengeOpen
    && !isMyTurn && !challengerId && !challengeUsed && !challengeDeclined && !answered;

  // Turn timer removed: the active G1 player now has unlimited time to answer or pass.

  // Challenge-window countdown (5s). Shown as large white numbers to opponents.
  useEffect(() => {
    if (!challengeOpen || !challengeEndsAt) { setChallengeLeft(null); return; }
    const end = new Date(challengeEndsAt).getTime();
    const tick = () => setChallengeLeft(Math.max(0, Math.ceil((end - Date.now()) / 1000)));
    tick();
    const iv = setInterval(tick, 200);
    return () => clearInterval(iv);
  }, [challengeOpen, challengeEndsAt]);

  // Suspense tick on each second of the challenge countdown (higher pitch in the final 2s).
  useEffect(() => {
    if (!challengeOpen || challengeLeft == null || challengeLeft <= 0) return;
    playTick(challengeLeft <= 2);
  }, [challengeLeft, challengeOpen]);

  // Does audio/video play ON this device? (G1/G2 with audioSource=players, or G4)
  const playsOnDevice = !!room && (
    ((room.gameType === 1 || room.gameType === 2) && room.audioSource === "players") ||
    room.gameType === 4
  );

  // Keep a stable handle to the socket send fn so callbacks/effects can use it.
  useEffect(() => { sendRef.current = send; }, [send]);
  // Sync audioEnabled into a ref so player callbacks read the latest value.
  useEffect(() => { audioEnabledRef.current = audioEnabled; }, [audioEnabled]);

  // Reset per-round state whenever a NEW round starts, regardless of HOW the
  // update arrived (ROUND_STARTED, or HELLO on a mid-game WS reconnect, or the
  // GET re-sync). Fixes the stuck "ANSWERED" overlay + dead buzz button when a
  // reconnect delivers the next round via HELLO (which doesn't reset answered).
  useEffect(() => {
    if (room?.status !== "in_round") return;
    const r = room?.currentRound ?? null;
    if (r == null || lastRoundRef.current === r) return;
    lastRoundRef.current = r;
    setBuzzerPlayerId(null);
    setAnswered(false);
    setAnswer({ artist: "", track: "", year: "" });
    setTruth(null);
    setVerdicts({});
    setChallengeUsed(false);
    setSubmittedAnswer(null);
    setChallengerId(null);
    setAnsweredIds([]);
    setPaused(false);
    setAutoNextIn(null);
  }, [room?.currentRound, room?.status]);

  // The device YT player finished initializing. If the user has already tapped
  // "Ready to Play", start the current song immediately; otherwise just buffer
  // (cue) it so the very next tap can play it synchronously (iOS requirement).
  const handleClipReady = () => {
    clipReadyRef.current = true;
    const vid = room?.currentSong?.videoId;
    if (!vid || !playsOnDevice) return;
    loadedVideoRef.current = vid;
    syncRoundRef.current = null;
    if (audioEnabledRef.current) {
      try { clipRef.current?.load(vid); } catch {}   // loadVideoById autoplays + unmutes
    } else {
      try { clipRef.current?.cue(vid); } catch {}     // pre-buffer, silent
    }
  };

  // Report buffer/cue state so the host can synchronize the start across devices.
  // 5 = CUED, 3 = BUFFERING. When we're waiting to sync a round, tell the host we're ready.
  const handleClipState = (data) => {
    if ((data === 5 || data === 3) && syncRoundRef.current != null) {
      try { sendRef.current?.({ type: "AUDIO_READY", round: syncRoundRef.current }); } catch {}
    }
  };

  // On a NEW round (clip already unlocked), buffer the next song and wait for the
  // host's synchronized AUDIO_PLAY. Fixes both the round-2 silence and cross-device sync.
  useEffect(() => {
    const vid = room?.currentSong?.videoId;
    if (!audioEnabled || !playsOnDevice || !vid || !clipReadyRef.current) return;
    if (loadedVideoRef.current === vid) return;
    loadedVideoRef.current = vid;
    syncRoundRef.current = room?.currentRound ?? null;
    try { clipRef.current?.cue(vid); } catch {}
  }, [room?.currentSong?.videoId, room?.currentRound, audioEnabled, playsOnDevice]);

  const submitAnswer = async () => {
    if (answered) return;
    try {
      await api.post(`/rooms/${code}/answer`, { playerId, artist: answer.artist, track: answer.track, year: answer.year ? parseInt(answer.year) : null });
      setAnswered(true);
      toast.success(t("player.answerSubmitted"));
    } catch {
      toast.error("Failed to submit");
    }
  };

  // Pause the G4 video the moment anyone buzzes
  useEffect(() => {
    if (room?.gameType === 4 && buzzerPlayerId) {
      try { clipRef.current?.pause(); } catch {}
    }
  }, [buzzerPlayerId, room?.gameType]);

  // Pause the device clip on reveal / finish so the song doesn't play over the answer.
  useEffect(() => {
    if (room?.status === "reveal" || room?.status === "finished") {
      try { clipRef.current?.pause(); } catch {}
    }
  }, [room?.status]);

  // THE user gesture. On iOS Safari playback must be triggered synchronously in
  // this click handler on an ALREADY-CREATED YouTube player. The DeviceClip is
  // mounted during the gate screen, so the player exists by now — we call
  // load()/play() right here, in the same event, to unlock audio for the session.
  const handleReadyTap = () => {
    playMagic();
    setAudioEnabled(true);
    audioEnabledRef.current = true;
    try { sessionStorage.setItem(`hm_ready_${code}`, "1"); } catch {}
    // Tell the host this player is ready. Retry a few times in case the WS
    // isn't OPEN yet — send() silently drops messages on a non-open socket.
    let readyTries = 0;
    const sendReady = () => {
      try { sendRef.current?.({ type: "PLAYER_READY" }); } catch {}
      if (status !== "open" && readyTries < 10) {
        readyTries += 1;
        setTimeout(sendReady, 400);
      }
    };
    sendReady();
    const c = clipRef.current;
    const vid = room?.currentSong?.videoId;
    if (c) {
      c.unmute();
      if (vid && playsOnDevice) {
        c.load(vid);            // loadVideoById plays synchronously within the gesture
        loadedVideoRef.current = vid;
        syncRoundRef.current = null;
      } else {
        c.play();               // establish playback activation for the whole session
      }
    }
    // Re-sync room state (async) so a mid-game join immediately picks up the live round.
    (async () => {
      try {
        const { data } = await api.get(`/rooms/${code}`);
        setRoom((r) => ({ ...(r || {}), ...data, gameType: data.currentGameType ?? data.gameType }));
        if (Array.isArray(data.yearChoices)) setYearChoices(data.yearChoices);
        if (data.albumArtwork) setMosaicArtwork(data.albumArtwork);
        if (Array.isArray(data.mosaicOrder)) setMosaicOrder(data.mosaicOrder);
      } catch {}
    })();
  };

  const pressBuzz = () => {
    setBuzzPressed(true);
    setTimeout(() => setBuzzPressed(false), 200);
    playHorn();
    if (room?.status !== "in_round") return;
    if (room?.gameType === 2 || room?.gameType === 3 || room?.gameType === 4) {
      send({ type: "BUZZ" });
    } else if (room?.gameType === 1) {
      if (room?.mode === "board") { submitAnswer(); return; }
      // Turn-based: only the active player or the current challenger may submit.
      if (activePlayerId === playerId || challengerId === playerId) submitAnswer();
      else toast(t("player.notYourTurn"), { icon: "⏳" });
    }
  };

  const pressChallenge = () => {
    if (challengeUsed) return;
    playWhistle();
    const target = isTurnBased ? activePlayerId : buzzerPlayerId;
    send({ type: "CHALLENGE", targetPlayerId: target });
    setChallengeUsed(true);
    toast(t("player.challengeSent"), { icon: "⚡" });
  };

  const pressRed = () => {
    playWhistle();
    // pass / skip - just clears local answer draft
    setAnswer({ artist: "", track: "", year: "" });
  };

  // "Weet ik niet" — pass the round for this player.
  // G1: submit empty answer, let auto-reveal handle when everyone's in.
  // G2/G3/G4: submit empty answer + force reveal immediately (skip 15s grace),
  // unless a challenger is already in progress (then wait for them).
  const pressDontKnow = async () => {
    if (answered) return;
    playWhistle();
    const turnBased = room?.gameType === 1 && room?.mode !== "board";
    const myTurn = activePlayerId === playerId;
    try {
      if (turnBased && myTurn && !challengeOpen) {
        // Active player passes -> turn moves to the next player (same song).
        await api.post(`/rooms/${code}/pass`, { playerId });
        toast(t("player.passedTurn"), { icon: "⏭️" });
        return;
      }
      await api.post(`/rooms/${code}/answer`, { playerId, artist: "", track: "", year: null });
      setAnswered(true);
      const isBuzzGame = room?.gameType === 2 || room?.gameType === 3 || room?.gameType === 4;
      if (isBuzzGame && !challengerId) {
        try { await api.post(`/rooms/${code}/reveal`, {}); } catch {}
      }
      toast("Doorgegeven", { icon: "🤷" });
    } catch {
      toast.error("Kon niet doorgeven");
    }
  };

  if (!room) return <div className="min-h-screen flex items-center justify-center font-display text-navy">{t("host.loading")}</div>;

  // Single, persistent device player. Mounted in BOTH the gate and game views
  // (as the first child of each top-level <div>, same key) so it stays alive
  // across the tap — its YT player is ready before the user taps Ready to Play.
  const clipVariant = (audioEnabled && room.status === "in_round" && room.gameType === 4) ? "video" : "hidden";
  const persistentClip = (
    <DeviceClip key="device-clip" ref={clipRef} variant={clipVariant} onReady={handleClipReady} onState={handleClipState} />
  );

  // Themed player backgrounds (synced from room.style via HELLO / STYLE_CHANGED).
  const playerTheme = getTheme(room.style || DEFAULT_STYLE);
  // Themed answer-button images (fall back to the default branded buttons).
  const btnPrimary = playerTheme.buttons?.primary || GREEN_BTN_URL;
  const btnChallenge = playerTheme.buttons?.challenge || RED_BTN_URL;
  const btnIdk = playerTheme.buttons?.idk || DONTKNOW_BTN_URL;
  // Rock & Roll uses a white paint-splatter ring around each button instead of
  // the clean white outline used by Hip & Retro.
  const splatter = playerTheme.buttonSplatter;
  const btnFxCls = splatter ? "hm-splatter-fg" : "hm-btn-outline";
  const usePlayVideo = room.status === "in_round" && !!playerTheme.playVideo;
  const useRevealVideo = (room.status === "reveal" || room.status === "finished") && !!playerTheme.revealVideo;
  // "Get ready" / waiting-for-host state: match the host's first (lobby) screen.
  const useLobbyVideo = (room.status !== "in_round" && room.status !== "reveal" && room.status !== "finished") && !!playerTheme.lobbyVideo;
  const videoBgActive = usePlayVideo || useRevealVideo || useLobbyVideo;
  // When a themed video background is active, suppress the static .player-bg
  // illustration so it doesn't flash through before the video renders.
  const solidBgStyle = videoBgActive ? { backgroundImage: "none", backgroundColor: "#0d1424" } : {};
  const gameRootStyle = { position: "relative", zIndex: 0, ...solidBgStyle };
  const gameImgUrl = (room.status === "reveal" || room.status === "finished") ? playerTheme.player.reveal : playerTheme.player.game;
  const gameBgKey = usePlayVideo ? `pv-${playerTheme.id}` : `img-${room.status}-${playerTheme.id}`;

  // "Ready to Play" gate — the player must tap once so mobile browsers register
  // the user gesture, unlocking YouTube audio autoplay. Until they tap, the
  // rest of the game UI is not rendered (audio pipeline can't start anyway).
  if (!audioEnabled) {
    const readyBg = playerTheme.readyVideo || playerTheme.joinVideo;
    return (
      <div
        className="player-bg flex flex-col items-center justify-center px-6 py-8"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: "100vw",
          height: "100dvh",
          overflowY: "auto",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch",
          ...(readyBg ? { backgroundImage: "none", backgroundColor: "#0d1424" } : bgImageStyle(playerTheme.player.join)),
        }}
        data-testid="player-ready-gate"
      >
        <LobbyMusic styleId={playerTheme.id} />
        {readyBg && (
          <>
            <video
              data-testid="player-ready-bg-video"
              autoPlay loop muted playsInline preload="auto" aria-hidden
              className="animate-bg-fade"
              style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, width: "100%", height: "100%", objectFit: "cover", zIndex: 0, pointerEvents: "none", filter: "saturate(1.22) contrast(1.06) brightness(1.03)" }}
              src={readyBg}
            />
            <div
              aria-hidden
              style={{
                position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 0, pointerEvents: "none",
                background: "rgba(14, 18, 40, 0.28)",
                backdropFilter: "blur(5px)",
                WebkitBackdropFilter: "blur(5px)",
              }}
            />
          </>
        )}
        {persistentClip}
        <div className="flex flex-col items-center gap-6 max-w-md w-full relative" style={{ zIndex: 1 }}>
          <HitmasterLogo className="h-24 sm:h-32 animate-float drop-shadow-[0_4px_0_rgba(0,0,0,0.35)]" />
          <div className="bg-navy/50 backdrop-blur-lg border-4 border-navy rounded-3xl px-6 py-5 flex flex-col items-center gap-3 w-full">
            <div className="font-bebas text-white text-white-shadow text-4xl sm:text-5xl tracking-widest uppercase text-center leading-none">
              {t("player.readyToPlay")}
            </div>
            <div className="font-poppins text-cream/90 text-center text-sm" style={{ fontWeight: 300, lineHeight: 1.55 }}>
              {t("player.readyGreet")} <b style={{ fontWeight: 700 }}>{myName || t("player.playerLower")}</b>{t("player.readyBody")}<b style={{ fontWeight: 700 }}>{code}</b>.
            </div>
            {playerTheme.buttons?.primary ? (
              <button
                data-testid="player-ready-btn"
                onClick={handleReadyTap}
                aria-label={t("player.readyToPlay")}
                className="img-btn relative w-full max-w-[220px] aspect-square mt-1"
              >
                {splatter && <img src={splatter} alt="" aria-hidden draggable={false} className="hm-splatter" />}
                <img
                  src={btnPrimary}
                  alt={t("player.readyToPlay")}
                  className={`hm-btn-anim ${btnFxCls} w-full h-full object-contain pointer-events-none select-none`}
                  draggable={false}
                />
              </button>
            ) : (
              <button
                data-testid="player-ready-btn"
                onClick={handleReadyTap}
                className="hm-btn hm-btn-primary w-full mt-2 uppercase tracking-widest flex items-center justify-center gap-3"
                style={{ fontSize: "clamp(1.5rem, 5.5vw, 2.25rem)", padding: "1.1rem 1.25rem", borderRadius: "18px" }}
              >
                <span className="text-4xl leading-none">🔊</span>
                Ready to Play
              </button>
            )}
            <div className="font-poppins text-cream/70 text-center text-xs pt-1" style={{ fontWeight: 300 }}>
              Op mobiel is dit nodig om geluid af te spelen.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isBuzzedByMe = buzzerPlayerId === playerId;
  const buzzerName = room?.players?.find((p) => p.id === buzzerPlayerId)?.name;

  return (
    <div
      className="player-bg flex flex-col"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: "100vw",
        height: "100dvh",
        overflowY: "auto",
        overflowX: "hidden",
        WebkitOverflowScrolling: "touch",
        zIndex: cssFullscreen ? 9999 : undefined,
        ...gameRootStyle,
      }}
      data-testid="player-root"
    >
      <HomeScreenTip />
      {useRevealVideo
        ? <VideoBg key={`rv-${playerTheme.id}`} src={playerTheme.revealVideo} overlay={0.14} testid="player-reveal-bg-video" />
        : usePlayVideo
          ? <VideoBg key={gameBgKey} src={playerTheme.playVideo} testid="player-play-bg-video" />
          : useLobbyVideo
            ? <VideoBg key={`lobby-${playerTheme.id}`} src={playerTheme.lobbyVideo} testid="player-lobby-bg-video" />
            : <div key={gameBgKey} aria-hidden className="animate-bg-fade" style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: -1, ...bgImageStyle(gameImgUrl) }} />
      }
      {persistentClip}
      {/* Logo at top (scrolls away) */}
      <div className="flex justify-center" style={{ paddingTop: "10px", paddingBottom: "6px" }}>
        <HitmasterLogo className="h-16 sm:h-20 animate-float drop-shadow-[0_3px_0_rgba(0,0,0,0.35)]" />
      </div>
      {/* Transparent header — clean & minimal so it doesn't distract from the game */}
      <div className="sticky top-0 z-20">
        <div className="px-3 py-1 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {(room.status === "in_round" || room.status === "reveal") && (
              <GameTypeBadge gameType={room.gameType} bare sizeClass="w-7 h-7 sm:w-8 sm:h-8" className="shrink-0" />
            )}
            <div
              className="shrink min-w-0 rounded-full border-2 px-4 py-1 font-bebas text-white text-base sm:text-lg uppercase truncate tracking-wide shadow-[0_4px_12px_rgba(0,0,0,0.35)] flex items-center gap-2"
              style={{ backgroundColor: "#1E1B4B", borderColor: playerColor(room.players, playerId), textShadow: "0 1px 3px rgba(0,0,0,0.55)" }}
              data-testid="player-name"
            >
              <span className="inline-block w-3 h-3 rounded-full shrink-0 border border-white/70" style={{ backgroundColor: playerColor(room.players, playerId) }} data-testid="player-own-color-dot" />
              <span className="truncate">{myName} · <span className="text-gold font-black">{me?.score ?? 0} pts</span></span>
            </div>
          </div>
          <div className="shrink-0 flex items-center gap-3">
            {(room.status === "in_round" || room.status === "reveal") && (room.totalRounds || 0) > 0 && (
              <div className="font-bebas text-white text-white-shadow text-base sm:text-lg tracking-widest whitespace-nowrap" data-testid="player-round-counter">
                Song {room.currentRound || 0} / {room.totalRounds || 0}
              </div>
            )}
            <button
              data-testid="player-fullscreen-toggle"
              onClick={toggleFullscreen}
              aria-label={isFs ? "Verlaat fullscreen" : "Volledig scherm"}
              title={isFs ? "Verlaat fullscreen" : "Volledig scherm"}
              className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/30 border-2 border-white/40 flex items-center justify-center text-white transition-colors"
            >
              {isFs ? <Minimize2 className="w-4 h-4" strokeWidth={2.5} /> : <Maximize2 className="w-4 h-4" strokeWidth={2.5} />}
            </button>
          </div>
        </div>
      </div>

      {/* Main area — center vertically for board mode (buzzer alone), top-align for phone modes (input card visible) */}
      <div className={`flex-1 flex flex-col items-center ${room.mode === "board" ? "justify-center" : "justify-start"} px-4 py-4 relative overflow-y-auto`}>
        <AnimatePresence mode="wait">
          {room.status === "reveal" && truth ? (
            <motion.div key="reveal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="w-full flex flex-col items-center gap-3">
              <VinylReveal artwork={truth.artwork} artist={truth.artist} track={truth.track} year={truth.year} album={truth.collection} mode={room.gameType === 3 ? "g3" : "g1"} onDark compact />
              {/* Live standings — total scores of ALL players, highest first. Two-column
                  grid (4+ players) with compact rows so all 8 fit without scrolling.
                  Semi-transparent so the reveal video shows through; own row highlighted.
                  Stays up for the whole reveal phase (until the next song starts). */}
              {standings.length > 0 && (
                <div
                  data-testid="reveal-standings"
                  className="w-full max-w-md rounded-2xl border-2 border-white/25 bg-navy/70 backdrop-blur-md px-2.5 py-2.5 shadow-[0_12px_34px_rgba(0,0,0,0.45)]"
                >
                  <div className="text-center font-bebas text-white text-white-shadow text-xl tracking-widest uppercase mb-2 flex items-center justify-center gap-2">
                    <Trophy className="w-5 h-5 text-gold" strokeWidth={2.5} /> {t("player.standings")}
                  </div>
                  <div className={`grid gap-1.5 ${standings.length >= 4 ? "grid-cols-2" : "grid-cols-1"}`}>
                    {standings.map((p, i) => {
                      const col = playerColor(room.players, p.id);
                      const isMe = p.id === playerId;
                      return (
                        <div
                          key={p.id}
                          data-testid={`standing-row-${p.id}`}
                          className={`flex items-center gap-1.5 rounded-lg pl-1.5 pr-2 py-1 transition-colors ${isMe ? "bg-white/25 ring-2 ring-gold" : "bg-white/10"}`}
                          style={{ borderLeft: `5px solid ${col}` }}
                        >
                          <span className="w-5 h-5 rounded-full bg-navy text-cream font-display font-black flex items-center justify-center text-[10px] shrink-0">
                            {i + 1}
                          </span>
                          <span className={`font-display font-bold text-sm truncate flex-1 min-w-0 ${isMe ? "text-white" : "text-white/90"}`}>
                            {p.name}
                            {isMe && <span className="ml-1 text-[9px] font-black text-gold align-middle">({t("player.you")})</span>}
                            {p.connected === false && !p.isBot && <span className="ml-1 text-[9px] font-bold text-coral align-middle">●</span>}
                          </span>
                          <CountUp
                            value={p.score}
                            from={p.score - (verdicts?.[p.id]?.points || 0)}
                            data-testid={`standing-score-${p.id}`}
                            className="font-display font-black text-white text-base tabular-nums shrink-0"
                            style={{ textShadow: "0 1px 3px rgba(0,0,0,0.55)" }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {verdicts[playerId] && (() => {
                const v = verdicts[playerId];
                const b = v.breakdown;
                const md = v.mode || (room.gameType === 3 ? "g3" : room.gameType === 2 ? "g2" : room.gameType === 4 ? "g4" : "g1");
                const ICONS = { year: Calendar, artist: Mic, track: Music2, album: Disc3, bonus: Star };
                let rows = [];
                if (b) {
                  if (md === "g3") {
                    rows = [["artist", t("player.artistColon"), b.artist], ["album", t("player.albumColon"), b.album]];
                  } else if (md === "g2") {
                    rows = [["artist", t("player.artistColon"), b.artist], ["track", t("player.titleColon"), b.track]];
                  } else {
                    rows = [["year", t("player.yearColon"), b.year], ["artist", t("player.artistColon"), b.artist], ["track", t("player.titleColon"), b.track]];
                  }
                  if (b.bonus > 0) rows.push(["bonus", t("player.brkBonus"), b.bonus]);
                }
                return (
                  <div className="w-full max-w-xs flex flex-col items-center gap-2" data-testid="my-verdict-wrap">
                    <div
                      className={`tactile w-full text-center ${v.points > 0 ? "bg-teal text-white" : "bg-coral text-white"} px-6 py-3 font-display font-black text-xl`}
                      data-testid="my-verdict"
                    >
                      {v.points > 0 ? `+${v.points} pts${v.yearOk ? ` ${t("player.yearCorrect")}` : ""}` : t("player.noPoints")}
                    </div>
                    {rows.length > 0 && (
                      <div
                        className="w-full rounded-2xl border-2 border-white/25 bg-navy/55 backdrop-blur-sm px-3 py-2 space-y-1"
                        data-testid="my-verdict-breakdown"
                      >
                        {rows.map(([key, label, pts]) => {
                          const Icon = ICONS[key] || Star;
                          const earned = (pts || 0) > 0;
                          return (
                            <div
                              key={key}
                              data-testid={`verdict-row-${key}`}
                              className="flex items-center justify-between gap-3 font-display"
                            >
                              <span className={`flex items-center gap-2 ${earned ? "text-white" : "text-white/45"}`}>
                                <Icon className="w-4 h-4" strokeWidth={2.4} />
                                <span className="font-bold text-sm">{label}</span>
                              </span>
                              <span className={`font-black text-sm ${earned ? "text-teal-200" : "text-white/40"}`}>
                                +{pts || 0} pts
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}
              {autoNextIn != null && autoNextIn > 0 && (
                <div className="font-bebas text-white text-white-shadow text-2xl tracking-widest" data-testid="player-auto-next-countdown">
                  {paused ? t("player.paused") : `${t("player.nextSongIn")} ${autoNextIn}s…`}
                </div>
              )}
              {roundStat && (
                <div
                  data-testid="player-round-stat"
                  className="rounded-2xl border-2 border-white/30 bg-navy/55 backdrop-blur-sm px-5 py-3 text-center max-w-xs"
                >
                  <div className="text-[10px] font-bold uppercase tracking-widest text-white/60">{t("stat.title")}</div>
                  <div className="font-bebas text-white text-white-shadow text-xl tracking-wide leading-tight">
                    {(() => { let x = t(`stat.${roundStat.key}`); if (roundStat.name != null) x = x.replace("{name}", roundStat.name); if (roundStat.year != null) x = x.replace("{year}", roundStat.year); return `${roundStat.emoji || ""} ${x}`.trim(); })()}
                  </div>
                </div>
              )}
            </motion.div>
          ) : room.status === "finished" ? (
            <motion.div key="fin" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center w-full max-w-md flex flex-col items-center">
              <div className="font-bebas text-white text-white-shadow text-6xl mb-3 tracking-wide">🏆</div>
              <div className="font-bebas text-white text-white-shadow text-3xl tracking-wide">{t("player.winner")}: {[...(room.players || [])].sort((a,b)=>b.score-a.score)[0]?.name || "—"}</div>
              {(() => {
                const hist = room.history || [];
                let a = 0, tt = 0, y = 0, pts = 0;
                hist.forEach((h) => {
                  const s = (h.scorers || []).find((x) => x.playerId === playerId);
                  if (s) { if (s.artistOk) a++; if (s.trackOk) tt++; if (s.yearOk) y++; pts += s.points || 0; }
                });
                const me = (room.players || []).find((p) => p.id === playerId);
                return (
                  <div data-testid="player-my-stats" className="mt-5 w-full rounded-2xl border-2 border-white/25 bg-navy/70 backdrop-blur-md px-4 py-4">
                    <div className="font-bebas text-white text-white-shadow text-2xl tracking-widest uppercase mb-3">Mijn statistieken</div>
                    <div className="grid grid-cols-3 gap-2">
                      {[["Artiest", a], ["Titel", tt], ["Jaar", y]].map(([lbl, val]) => (
                        <div key={lbl} className="rounded-xl bg-white/10 py-2" data-testid={`mystat-${lbl}`}>
                          <div className="font-display font-black text-white text-3xl">{val}</div>
                          <div className="text-[10px] font-bold uppercase tracking-widest text-white/60">{lbl} goed</div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 font-display font-black text-gold text-xl">{me?.score ?? pts} punten</div>
                  </div>
                );
              })()}
              <button onClick={() => nav("/")} className="mt-6 bg-navy text-cream font-bebas uppercase px-6 py-2 rounded-full border-2 border-cream shadow-[0_4px_0_#000] tracking-widest">{t("player.home")}</button>
            </motion.div>
          ) : room.status === "in_round" ? (
            <motion.div key="play" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-full max-w-xl flex flex-col items-center gap-4">
              <div
                className="font-bebas text-white text-white-shadow uppercase tracking-widest flex items-center gap-3"
                style={{ fontSize: "clamp(2rem, 9vw, 3.75rem)", lineHeight: 1 }}
              >
                <Equalizer /> {room.mode === "board" ? t("player.hdrSlap") : (room.gameType === 3 ? t("player.hdrG3") : (room.gameType === 4 ? t("player.hdrG4") : (room.gameType === 2 ? t("player.hdrG2") : t("player.hdrG1"))))} <Equalizer />
              </div>

              {isTurnBased && (
                <div
                  data-testid="current-turn-banner"
                  className="w-full max-w-md rounded-2xl border-2 px-4 py-2 text-center font-bebas tracking-widest uppercase"
                  style={{
                    backgroundColor: isMyTurn ? "#2BB3A3" : "rgba(30,27,75,0.72)",
                    borderColor: "#FFF9EE",
                    color: "#FFF9EE",
                    textShadow: "0 2px 6px rgba(0,0,0,0.5)",
                    boxShadow: "0 6px 16px rgba(0,0,0,0.35)",
                  }}
                >
                  {isMyTurn ? (
                    <span className="text-lg sm:text-2xl">{t("player.yourTurnBig")}</span>
                  ) : (
                    <span className="text-base sm:text-xl">{t("player.currentTurn")}: <span className="text-gold">{activeName}</span></span>
                  )}
                  {/* turn timer removed — active player has unlimited time */}
                </div>
              )}

              {room.gameType === 3 && (
                <MosaicReveal
                  artwork={mosaicArtwork}
                  order={mosaicOrder}
                  frozen={!!buzzerPlayerId}
                  intervalMs={500}
                  size="min(58vw, 30vh, 260px)"
                />
              )}

              {/* Audio-only clip on player device — for G1/G2 when host chose audioSource=players.
                  The actual playback happens in the persistent DeviceClip; this is just the indicator. */}
              {(room.gameType === 1 || room.gameType === 2) && room.audioSource === "players" && room.currentSong?.videoId && (
                <div className="w-full max-w-md" data-testid="player-audio-clip">
                  <div className="rounded-2xl border-2 border-navy bg-navy/40 backdrop-blur-sm px-4 py-3 flex items-center gap-3">
                    <div className="text-3xl animate-pulse">🎵</div>
                    <div className="flex-1">
                      <div className="font-bebas text-white text-white-shadow text-lg tracking-widest uppercase leading-none">{t("player.musicOnDevice")}</div>
                      <div className="font-poppins text-cream/80 text-xs" style={{ fontWeight: 300 }}>{t("player.volumeUp")}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* G4 videoclip plays in the fixed banner (persistent DeviceClip, variant="video").
                  This spacer reserves the vertical room so the answer UI sits below the banner. */}
              {room.gameType === 4 && room.currentSong?.videoId && (
                <div className="w-full max-w-md" data-testid="player-video-clip" aria-hidden>
                  <div style={{ height: "min(49.5vw, 236px)" }} />
                </div>
              )}

              {room.mode === "board" ? null : room.gameType === 1 ? (
                ((isMyTurn && !challengeOpen && !answered) || (iAmChallengerG1 && !answered)) ? (
                <div className="w-full tactile p-3 sm:p-4 space-y-2" data-testid="g1-answer-form">
                  <div className="font-bebas text-navy text-lg tracking-wide" data-testid="g1-form-heading">
                    {iAmChallengerG1 ? t("player.yourChallenge") : t("player.yourTurnAll")}
                  </div>
                  <input
                    data-testid="player-answer-artist"
                    value={answer.artist}
                    onChange={(e) => setAnswer({ ...answer, artist: e.target.value })}
                    placeholder={t("player.artist")}
                    className="w-full bg-white/80 backdrop-blur-sm border-2 border-navy rounded-xl font-display font-black text-navy placeholder:text-navy/40 placeholder:font-bold focus:outline-none focus:ring-4 focus:ring-coral/40"
                    style={{ padding: "clamp(0.4rem, 2vw, 0.65rem) 1rem", fontSize: "clamp(1rem, 4.2vw, 1.45rem)" }}
                    disabled={answered}
                  />
                  <input
                    data-testid="player-answer-track"
                    value={answer.track}
                    onChange={(e) => setAnswer({ ...answer, track: e.target.value })}
                    placeholder={t("player.trackTitle")}
                    className="w-full bg-white/80 backdrop-blur-sm border-2 border-navy rounded-xl font-display font-black text-navy placeholder:text-navy/40 placeholder:font-bold focus:outline-none focus:ring-4 focus:ring-coral/40"
                    style={{ padding: "clamp(0.4rem, 2vw, 0.65rem) 1rem", fontSize: "clamp(1rem, 4.2vw, 1.45rem)" }}
                    disabled={answered}
                  />
                  {yearChoices.length === 3 ? (
                    <div data-testid="player-year-choices-g1">
                      <div className="grid grid-cols-3 gap-2">
                        {yearChoices.map((y) => {
                          const selected = String(answer.year) === String(y);
                          return (
                            <button
                              key={y}
                              type="button"
                              data-testid={`player-year-choice-${y}`}
                              onClick={() => setAnswer({ ...answer, year: String(y) })}
                              disabled={answered}
                              className={`tap py-2 rounded-xl border-2 border-navy font-display font-black text-lg transition-all ${selected ? "bg-teal text-white shadow-[0_4px_0_#1E1B4B]" : "bg-white/80 text-navy hover:bg-white"}`}
                            >
                              <span className={`inline-block w-4 h-4 rounded-sm border-2 border-current mr-2 align-middle text-xs leading-none ${selected ? "bg-white text-teal" : ""}`}>{selected ? "✓" : ""}</span>
                              {y}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <input
                      data-testid="player-answer-year"
                      value={answer.year}
                      onChange={(e) => setAnswer({ ...answer, year: e.target.value.replace(/\D/g,"").slice(0,4) })}
                      placeholder={t("player.yearBonus")}
                      inputMode="numeric"
                      className="w-full bg-white/80 backdrop-blur-sm border-2 border-navy rounded-xl font-display font-black text-navy placeholder:text-navy/40 placeholder:font-bold focus:outline-none focus:ring-4 focus:ring-coral/40"
                      style={{ padding: "clamp(0.4rem, 2vw, 0.65rem) 1rem", fontSize: "clamp(1rem, 4.2vw, 1.45rem)" }}
                      disabled={answered}
                    />
                  )}
                  {answered ? (
                    <div className="text-teal font-black uppercase text-base">{t("player.answered")}</div>
                  ) : null}
                </div>
                ) : (
                  <div className="w-full tactile p-4 text-center space-y-1" data-testid="g1-waiting">
                    {challengeOpen ? (
                      isMyTurn ? (
                        <div className="font-bebas text-navy text-lg tracking-wide" data-testid="g1-waiting-challenge">{t("player.waitingChallenge")}{challengeLeft != null ? ` (${challengeLeft})` : ""}</div>
                      ) : challengerId ? (
                        <div className="font-bebas text-coral text-lg tracking-wide">
                          {((room?.players || []).find((p) => p.id === challengerId)?.name || t("player.playerFallback"))} {t("player.challengesNow")}
                        </div>
                      ) : challengeAfterPass ? (
                        <div className="space-y-1" data-testid="g1-passed-challenge">
                          <div className="font-bebas text-navy/80 text-sm uppercase tracking-wide">
                            {activeName || t("player.playerFallback")} {t("player.passedShort")}
                          </div>
                          <div className="font-bebas text-coral text-base tracking-wide">{t("player.passChallengeCta")}</div>
                        </div>
                      ) : (
                        <div className="space-y-2" data-testid="g1-submitted-answer">
                          {submittedAnswer && (
                            <>
                              <div className="font-bebas text-navy/80 text-sm uppercase tracking-wide">
                                {((room?.players || []).find((p) => p.id === submittedAnswer.playerId)?.name || activeName || t("player.playerFallback"))} {t("player.says")}:
                              </div>
                              <div className="font-display font-black text-navy text-xl leading-tight" data-testid="g1-submitted-text">
                                {submittedAnswer.artist || "—"} <span className="opacity-50">-</span> {submittedAnswer.track || "—"}
                                {submittedAnswer.year != null && submittedAnswer.year !== "" ? ` (${submittedAnswer.year})` : ""}
                              </div>
                            </>
                          )}
                          <div className="font-bebas text-coral text-base tracking-wide">{t("player.answerGivenChallenge")}</div>
                        </div>
                      )
                    ) : (
                      <div className="font-bebas text-navy text-lg tracking-wide">
                        {t("player.turnOf")} <span className="text-coral">{activeName}</span>
                      </div>
                    )}
                    {!challengeOpen && <div className="text-navy/70 font-bold text-xs">{t("player.waitYourTurn")}</div>}
                  </div>
                )
              ) : (
                // GAME TYPE 2/3/4 — artist + track/album (+ year) with buzz-holder / challenger flows
                (() => {
                  const isG3 = room.gameType === 3;
                  const isG4 = room.gameType === 4;
                  const trackLabel = isG3 ? t("player.albumTitleBonus") : t("player.trackTitle");
                  const iAmBuzzer = isBuzzedByMe;
                  const iAmChallenger = challengerId === playerId;
                  const showMyInput = (iAmBuzzer && !answered) || (iAmChallenger && !answered);
                  const showSubmittedAnswerForOthers = submittedAnswer && !iAmBuzzer && !iAmChallenger;
                  if (showMyInput) {
                    return (
                      <div className="w-full tactile p-4 space-y-2" data-testid="g2-answer-form">
                        <div className="font-bebas text-navy text-lg tracking-wide">
                          {iAmChallenger ? t("player.yourChallenge") : (isG3 ? t("player.yourTurnArtist") : (isG4 ? t("player.yourTurnAll") : t("player.yourTurnArtistTitle")))}
                        </div>
                        <input data-testid="player-answer-artist" value={answer.artist} onChange={(e) => setAnswer({ ...answer, artist: e.target.value })} placeholder={t("player.artist")}
                          className="w-full bg-white/80 backdrop-blur-sm border-2 border-navy rounded-xl font-display font-black text-navy placeholder:text-navy/40 placeholder:font-bold focus:outline-none focus:ring-4 focus:ring-coral/40"
                          style={{ padding: "clamp(0.4rem, 2vw, 0.65rem) 1rem", fontSize: "clamp(1rem, 4.2vw, 1.45rem)" }}
                          disabled={answered} />
                        <input data-testid="player-answer-track" value={answer.track} onChange={(e) => setAnswer({ ...answer, track: e.target.value })} placeholder={trackLabel}
                          className="w-full bg-white/80 backdrop-blur-sm border-2 border-navy rounded-xl font-display font-black text-navy placeholder:text-navy/40 placeholder:font-bold focus:outline-none focus:ring-4 focus:ring-coral/40"
                          style={{ padding: "clamp(0.4rem, 2vw, 0.65rem) 1rem", fontSize: "clamp(1rem, 4.2vw, 1.45rem)" }}
                          disabled={answered} />
                        {isG4 && yearChoices.length === 3 && (
                          <div className="pt-1" data-testid="player-year-choices">
                            <div className="grid grid-cols-3 gap-2">
                              {yearChoices.map((y) => {
                                const selected = String(answer.year) === String(y);
                                return (
                                  <button
                                    key={y}
                                    type="button"
                                    data-testid={`player-year-choice-${y}`}
                                    onClick={() => setAnswer({ ...answer, year: String(y) })}
                                    disabled={answered}
                                    className={`tap py-2 rounded-xl border-2 border-navy font-display font-black text-lg transition-all ${selected ? "bg-teal text-white shadow-[0_4px_0_#1E1B4B]" : "bg-white/80 text-navy hover:bg-white"}`}
                                  >
                                    <span className={`inline-block w-4 h-4 rounded-sm border-2 border-current mr-2 align-middle text-xs leading-none ${selected ? "bg-white text-teal" : ""}`}>{selected ? "✓" : ""}</span>
                                    {y}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        <div className="text-xs text-navy/70 font-bold pt-1">
                          {t("player.pointsLabel")}: {isG3 ? t("player.ptsG3") : (isG4 ? t("player.ptsG4") : t("player.ptsG2"))}
                        </div>
                        <button
                          data-testid="player-answer-submit"
                          onClick={submitAnswer}
                          disabled={answered || (!answer.artist && !answer.track)}
                          className="tap w-full bg-navy text-cream font-bebas uppercase text-lg py-2 rounded-lg tracking-wider border-2 border-cream disabled:opacity-40"
                        >
                          {t("player.submitAnswer")}
                        </button>
                      </div>
                    );
                  }
                  if (showSubmittedAnswerForOthers) {
                    const buzzName = room?.players?.find((p) => p.id === submittedAnswer.playerId)?.name || t("player.playerFallback");
                    return (
                      <div className="w-full tactile p-4 space-y-2" data-testid="g2-submitted-visible">
                        <div className="font-bebas text-navy text-sm tracking-wide uppercase">{buzzName} {t("player.answeredColon")}</div>
                        <div className="font-display font-black text-navy text-lg">
                          <span className="opacity-70">{t("player.artistColon")}:</span> {submittedAnswer.artist || <span className="italic opacity-50">—</span>}
                        </div>
                        <div className="font-display font-black text-navy text-lg">
                          <span className="opacity-70">{isG3 ? t("player.albumColon") : t("player.titleColon")}:</span> {submittedAnswer.track || <span className="italic opacity-50">—</span>}
                        </div>
                        {isG4 && submittedAnswer.year != null && (
                          <div className="font-display font-black text-navy text-lg">
                            <span className="opacity-70">{t("player.yearColon")}:</span> {submittedAnswer.year}
                          </div>
                        )}
                        {!challengerId ? (
                          <div className="text-navy/70 font-bold text-xs pt-1">{t("player.thinkWrong1")}<span className="text-coral">{t("player.thinkWrongRed")}</span>{t("player.thinkWrong2")}</div>
                        ) : (
                          <div className="text-coral font-black text-sm pt-1">{t("player.challengeBusy")}</div>
                        )}
                      </div>
                    );
                  }
                  if (answered && (iAmBuzzer || iAmChallenger)) {
                    return (
                      <div className="font-poppins font-bold text-white text-white-shadow text-center max-w-xs text-[14pt] leading-snug">
                        {t("player.waitingReveal")}
                      </div>
                    );
                  }
                  return (
                    <div className="font-poppins font-bold text-white text-white-shadow text-center max-w-xs text-[14pt] leading-snug" data-testid="listen-carefully">
                      {t("player.listenBuzz")}
                    </div>
                  );
                })()
              )}

              {/* GREEN BIG BUZZER — HitMaster branded image, scales with viewport */}
              <button
                type="button"
                data-testid="player-buzz-button"
                onClick={pressBuzz}
                aria-pressed={buzzPressed}
                className={`img-btn ${buzzPressed ? "pressed" : ""} relative flex items-center justify-center`}
                style={{
                  width: room.mode === "board"
                    ? "min(67vw, 55vh, 546px)"
                    : (room.gameType === 3
                        ? "min(49vw, 30vh, 280px)"
                        : (room.gameType === 4
                            ? "min(36vw, 22vh, 210px)"
                            : (room.gameType === 1
                                ? "min(49vw, 38vh, 322px)"
                                : "min(60vw, 55vh, 434px)"))),
                  height: room.mode === "board"
                    ? "min(67vw, 55vh, 546px)"
                    : (room.gameType === 3
                        ? "min(49vw, 30vh, 280px)"
                        : (room.gameType === 4
                            ? "min(36vw, 22vh, 210px)"
                            : (room.gameType === 1
                                ? "min(49vw, 38vh, 322px)"
                                : "min(60vw, 55vh, 434px)"))),
                  margin: room.mode === "board" ? "auto" : undefined,
                  filter: "drop-shadow(0 12px 0 rgba(0,0,0,0.35)) drop-shadow(0 20px 30px rgba(0,0,0,0.25))",
                  touchAction: "manipulation",
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                {splatter && <img src={splatter} alt="" aria-hidden draggable={false} className="hm-splatter" />}
                <img
                  src={btnPrimary}
                  alt="Buzz"
                  className={`hm-btn-anim ${btnFxCls} w-full h-full object-contain pointer-events-none select-none`}
                  draggable={false}
                />
                {(room.gameType === 2 || room.gameType === 4) && isBuzzedByMe && (
                  <div className="absolute inset-0 z-[2] flex items-center justify-center pointer-events-none">
                    <div className="bg-navy text-cream font-bebas text-3xl sm:text-5xl px-6 py-2 rounded-full border-4 border-cream tracking-widest -rotate-6">
                      {t("player.you")}
                    </div>
                  </div>
                )}
                {room.gameType === 1 && answered && (
                  <div className="absolute inset-0 z-[2] flex items-center justify-center pointer-events-none">
                    <div className="bg-navy/85 text-cream font-bebas text-2xl sm:text-4xl px-5 py-2 rounded-full border-4 border-cream tracking-widest">
                      {t("player.answeredBig")}
                    </div>
                  </div>
                )}
              </button>

              {buzzerName && (room.gameType === 2 || room.gameType === 4) && (
                <div className="font-bebas text-white text-white-shadow text-xl sm:text-2xl tracking-wide" data-testid="buzz-status">
                  {isBuzzedByMe ? t("player.youHaveMic") : `🔔 ${buzzerName} ${t("player.buzzedFirst")}`}
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div key="lobby" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
              <div className="font-bebas text-white text-white-shadow text-4xl sm:text-5xl mb-2 tracking-wide">{t("player.getReady")}</div>
              <div className="font-bebas text-white text-white-shadow text-xl tracking-wide">{t("player.waitingHost")}</div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Black circular "I don't know" button - bottom LEFT, mirror of the challenge button */}
      {room.status === "in_round" && (() => {
        const iAmBuzzer = isBuzzedByMe;
        const iAmChallenger = challengerId === playerId;
        const isBuzzGame = room.gameType === 2 || room.gameType === 3 || room.gameType === 4;
        // Can pass when:
        //   G1: I haven't answered yet
        //   G2/3/4: I have the mic (buzzer or challenger) and haven't answered
        const canPass = !answered && (
          isTurnBased
            ? ((isMyTurn && !challengeOpen) || iAmChallenger)
            : (!isBuzzGame ? true : (iAmBuzzer || iAmChallenger))
        );
        return (
          <button
            type="button"
            data-testid="player-dontknow-corner"
            onClick={() => { if (canPass) pressDontKnow(); }}
            className="img-btn fixed z-30 flex items-center justify-center"
            style={{
              width: "min(24vw, 24vh, 156px)",
              height: "min(24vw, 24vh, 156px)",
              left: "clamp(16px, 4vw, 40px)",
              bottom: "clamp(16px, 4vw, 40px)",
              filter: "brightness(1.12) drop-shadow(0 0 12px rgba(255,255,255,0.6)) drop-shadow(0 10px 0 rgba(0,0,0,0.4)) drop-shadow(0 16px 26px rgba(0,0,0,0.4))",
              opacity: canPass ? 1 : 0.6,
              cursor: canPass ? "pointer" : "not-allowed",
              touchAction: "manipulation",
              WebkitTapHighlightColor: "transparent",
            }}
            title={t("player.dontKnow")}
            aria-disabled={!canPass}
          >
            {splatter && <img src={splatter} alt="" aria-hidden draggable={false} className="hm-splatter" />}
            <img
              src={btnIdk}
              alt={t("player.dontKnow")}
              className={`hm-btn-anim ${btnFxCls} w-full h-full object-contain pointer-events-none select-none`}
              draggable={false}
            />
          </button>
        );
      })()}

      {/* Red Challenge button - bottom right (buzz games only; G1 uses the CHALLENGE? prompt) */}
      {room.status === "in_round" && !isTurnBased && (() => {
        const iAmBuzzer = isBuzzedByMe;
        const iAmChallenger = challengerId === playerId;
        const canChallenge = (room.gameType === 2 || room.gameType === 3 || room.gameType === 4)
          ? !!submittedAnswer && !iAmBuzzer && !challengerId && !challengeUsed
          : (!challengeUsed && !iAmBuzzer && !iAmChallenger);
        return (
          <button
            type="button"
            data-testid="player-challenge-button"
            onClick={() => { if (canChallenge) pressChallenge(); }}
            className="img-btn fixed z-30"
            style={{
              width: "min(24vw, 24vh, 156px)",
              height: "min(24vw, 24vh, 156px)",
              right: "clamp(16px, 4vw, 40px)",
              bottom: "clamp(16px, 4vw, 40px)",
              filter: "brightness(1.12) drop-shadow(0 0 12px rgba(255,255,255,0.6)) drop-shadow(0 10px 0 rgba(0,0,0,0.4)) drop-shadow(0 16px 26px rgba(0,0,0,0.4))",
              opacity: canChallenge ? 1 : 0.6,
              cursor: canChallenge ? "pointer" : "not-allowed",
              animationDelay: "0.4s",
              touchAction: "manipulation",
              WebkitTapHighlightColor: "transparent",
            }}
            title="Challenge"
            aria-disabled={!canChallenge}
          >
            {splatter && <img src={splatter} alt="" aria-hidden draggable={false} className="hm-splatter" />}
            <img
              src={btnChallenge}
              alt="Challenge"
              className={`hm-btn-anim ${btnFxCls} w-full h-full object-contain pointer-events-none select-none`}
              draggable={false}
            />
          </button>
        );
      })()}

      {/* G1 CHALLENGE? prompt — shown to opponents during the 5s window.
          Big white "CHALLENGE?" + countdown; YES (right) challenges, NO (left) declines. */}
      {canG1Challenge && (
        <div
          data-testid="challenge-prompt"
          className="fixed inset-0 z-40 flex flex-col items-center justify-center px-6"
          style={{ background: "rgba(4,8,20,0.55)", backdropFilter: "blur(3px)" }}
        >
          <div
            className="font-bebas text-white uppercase tracking-widest text-center leading-none"
            style={{ fontSize: "clamp(2.5rem, 12vw, 5rem)", textShadow: "0 4px 0 rgba(0,0,0,0.5), 0 0 18px rgba(0,0,0,0.6)" }}
          >
            {t("player.challengeQ")}
          </div>
          {/* The active player's guess (or that they passed) — shown big & high-contrast
              directly above the countdown so challengers can read it instantly. */}
          <div className="text-center max-w-xl mt-3 px-2" data-testid="challenge-active-guess">
            {challengeAfterPass ? (
              <div
                className="font-bebas text-white uppercase tracking-wide leading-tight"
                style={{ fontSize: "clamp(1.5rem, 6.5vw, 2.6rem)", textShadow: "0 3px 0 rgba(0,0,0,0.55), 0 0 16px rgba(0,0,0,0.7)" }}
                data-testid="challenge-passed-text"
              >
                {t("player.passedKnowsNothing").replace("{name}", activeName || t("player.playerFallback"))}
              </div>
            ) : submittedAnswer ? (
              <>
                <div
                  className="font-bebas text-white/85 uppercase tracking-widest leading-none"
                  style={{ fontSize: "clamp(0.95rem, 4vw, 1.35rem)", textShadow: "0 2px 6px rgba(0,0,0,0.6)" }}
                >
                  {(room?.players || []).find((p) => p.id === submittedAnswer.playerId)?.name || activeName || t("player.playerFallback")} {t("player.says")}
                </div>
                <div
                  className="font-display font-black text-white leading-tight mt-1"
                  style={{ fontSize: "clamp(1.6rem, 7vw, 3rem)", textShadow: "0 3px 0 rgba(0,0,0,0.55), 0 0 18px rgba(0,0,0,0.75)" }}
                  data-testid="challenge-guess-text"
                >
                  {submittedAnswer.artist || "—"} <span className="opacity-50">-</span> {submittedAnswer.track || "—"}
                  {submittedAnswer.year != null && submittedAnswer.year !== "" ? ` (${submittedAnswer.year})` : ""}
                </div>
              </>
            ) : null}
          </div>
          {challengeLeft != null && (
            <div
              data-testid="challenge-countdown"
              className="font-display font-black text-white tabular-nums leading-none mt-2"
              style={{ fontSize: "clamp(4rem, 22vw, 9rem)", textShadow: "0 6px 0 rgba(0,0,0,0.45), 0 0 26px rgba(0,0,0,0.65)" }}
            >
              {challengeLeft}
            </div>
          )}
          <div className="flex items-center justify-center gap-8 sm:gap-14 mt-6 w-full max-w-xl">
            {/* NO — left */}
            <button
              type="button"
              data-testid="challenge-no-btn"
              onClick={() => { playWhistle(); setChallengeDeclined(true); }}
              className="img-btn"
              style={{ width: "min(34vw, 30vh, 210px)", height: "min(34vw, 30vh, 210px)", touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
              title={t("player.challengeNo")}
            >
              <img src={CHALLENGE_NO_URL} alt={t("player.challengeNo")} draggable={false}
                className="hm-btn-anim w-full h-full object-contain pointer-events-none select-none"
                style={{ filter: "drop-shadow(0 10px 0 rgba(0,0,0,0.4)) drop-shadow(0 16px 26px rgba(0,0,0,0.45))" }} />
            </button>
            {/* YES — right */}
            <button
              type="button"
              data-testid="challenge-yes-btn"
              onClick={() => { pressChallenge(); }}
              className="img-btn"
              style={{ width: "min(34vw, 30vh, 210px)", height: "min(34vw, 30vh, 210px)", touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
              title={t("player.challengeYes")}
            >
              <img src={CHALLENGE_YES_URL} alt={t("player.challengeYes")} draggable={false}
                className="hm-btn-anim w-full h-full object-contain pointer-events-none select-none"
                style={{ filter: "drop-shadow(0 10px 0 rgba(0,0,0,0.4)) drop-shadow(0 16px 26px rgba(0,0,0,0.45))" }} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
